import React, { useContext, useEffect, useRef, useState } from "react";

import { Formio } from "@formio/react";
import { createRoot } from "react-dom/client";
import { useTranslation } from "react-i18next";
import { LuImageOff } from "react-icons/lu";
import { RxReload } from "react-icons/rx";
import { useLocation, useParams, useNavigate } from "react-router-dom";

import CfBreadcrumb from "../../common/CfBreadcrumb/CfBreadcrumb";
import CfLoader from "../../common/CfLoader/CfLoader";
import Dropdown from "../../common/Dropdown/Dropdown";
import {
  injectTemplatePreview,
  updateTemplatePreview,
} from "../../common/HtmlDesigner/preview/templatePreview";
import Toast from "../../common/Toast/Toast";
import SettingOption from "../../components/SettingOption/SettingOption";
import { imgRegex, whiteSpaceEmptyStrRegex } from "../../configs/App.Regex";
import EntityConstants from "../../configs/EntityConstants";
import { resolveTemplateSource } from "../../configs/templateTypes";
import { GET, POST } from "../../service/CRUDService";
import AuthContext from "../../store/auth-context";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import { hasFormDataAttributes } from "../../utils/formUtils/hasFormDataAttributes";

const CertificateDetails = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { credential_template_id } = useParams();
  const [certificateTitle, setCertificateTitle] = useState("");
  const [programmeName, setProgrammeName] = useState("");
  const [batchName, setBatchName] = useState("");
  const [selectedProgrammeCode, setSelectedProgrammeCode] = useState("");
  const [errorKey, setErrorKey] = useState("");
  const [programmeNameErrorKey, setProgrammeNameErrorKey] = useState("");
  const [batchNameErrorKey, setBatchNameErrorKey] = useState("");

  const [fieldsChanged, setFieldsChanged] = useState({});

  const [currentSVG, setCurrentSVG] = useState("");
  const [previewTemplateType, setPreviewTemplateType] = useState();
  const [certificateData, setCertificateData] = useState();
  const [orgLogo, setOrgLogo] = useState("");
  const [customFieldChanged, setCustomFieldChanged] = useState({});
  const [isToggleOn, setIsToggleOn] = useState(false);
  const lastPrefillRef = useRef(false);
  const valuesBeforePrefillRef = useRef(null);
  const [activeTab, setActiveTab] = useState("basic");
  const [formData, setFormData] = useState({});
  const [isAuthRequired, setIsAuthRequired] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [isPreAuth, setIsPreAuth] = useState(false);
  const preAuthUserEditedRef = useRef(false);
  const formRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [isPaymentRequired, setIsPaymentRequired] = useState(false);
  const [verificationCost, setVerificationCost] = useState("");
  const [isExpiryRequired, setIsExpiryRequired] = useState(false);
  const [expiryValue, setExpiryValue] = useState("");
  const [expiryUnit, setExpiryUnit] = useState("days");
  const [selectedComponents, setSelectedComponents] = useState([]);
  const location = useLocation();
  const authContext = useContext(AuthContext);
  const [programmeList, setProgrammeList] = useState([]);
  const [programmeNext, setProgrammeNext] = useState(null);
  const [programmeLoading, setProgrammeLoading] = useState(false);
  const [programmeDropdownOpen, setProgrammeDropdownOpen] = useState(false);
  const [programmeOffset, setProgrammeOffset] = useState(0);
  const programmeDropdownRef = useRef(null);
  const [batchList, setBatchList] = useState([]);
  const [batchNext, setBatchNext] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchDropdownOpen, setBatchDropdownOpen] = useState(false);
  const [batchOffset, setBatchOffset] = useState(0);
  const [hasFormFields, setHasFormFields] = useState(false);
  const [toggleMessage, setToggleMessage] = useState("");
  const [isInvalidCombination, setIsInvalidCombination] = useState(false);

  const batchDropdownRef = useRef(null);

  const breadcrumbItems = [
    {
      titleKey: "designs:edit",
      to: `/certificate/edit/${credential_template_id}`,
    },
  ];

  const expiryUnitOptions = [
    { name: t("designs:days"), value: "days" },
    { name: t("designs:months"), value: "months" },
    { name: t("designs:years"), value: "years" },
  ];

  const renderToContainer = (containerId, content) => {
    const container = document.getElementById(containerId);
    if (container) {
      const root = createRoot(container);
      root.render(content);
      return root;
    }
    return null;
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const calculateExpiryDays = (value, unit) => {
    if (value === "" || value === null || value === undefined) return null;

    const numericValue = Number(value);
    if (!Number.isSafeInteger(numericValue) || numericValue <= 0) return null;

    if (unit === "months") return numericValue * 30;
    if (unit === "years") return numericValue * 365;
    return numericValue;
  };

  const getExpiryStateFromData = (data) => {
    const expiryConfig = data?.expiry_config;
    const isExpiryEnabled = data?.is_expires === true;

    if (isExpiryEnabled && expiryConfig) {
      return {
        isExpiryRequired: true,
        expiryValue:
          expiryConfig.expires_value !== undefined &&
          expiryConfig.expires_value !== null
            ? String(expiryConfig.expires_value)
            : expiryConfig.expires_in_days !== undefined &&
                expiryConfig.expires_in_days !== null
              ? String(expiryConfig.expires_in_days)
              : "",
        expiryUnit: expiryConfig.expires_unit || "days",
      };
    }

    if (isExpiryEnabled && data?.value && data?.unit) {
      return {
        isExpiryRequired: true,
        expiryValue: String(data.value),
        expiryUnit: data.unit,
      };
    }

    if (isExpiryEnabled && data?.expires_in_days) {
      return {
        isExpiryRequired: true,
        expiryValue: String(data.expires_in_days),
        expiryUnit: "days",
      };
    }

    return {
      isExpiryRequired: false,
      expiryValue: "",
      expiryUnit: "days",
    };
  };

  const handlePaymentCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    localStorage.setItem("verCheck", isChecked);
    setIsPaymentRequired(isChecked);

    if (!isChecked) {
      localStorage.removeItem("verCost");
      setVerificationCost("");
    }
  };

  const handleVerificationCostChange = (e) => {
    const rupees = e.target.value; // Keep it as a string
    const paisa = Math.round(parseFloat(rupees || 0) * 100); // Convert safely
    localStorage.setItem("verCost", paisa);
    setVerificationCost(rupees); // Keep rupees as a string for input
  };

  const handleExpiryRequiredChange = (e) => {
    const isChecked = e.target.checked;
    setIsExpiryRequired(isChecked);

    if (!isChecked) {
      setExpiryValue("");
      setExpiryUnit("days");
    }
  };

  const handleExpiryValueChange = (e) => {
    setExpiryValue(e.target.value);
  };

  const handleExpiryUnitChange = (value) => {
    setExpiryUnit(value?.target ? value.target.value : value);
  };

  const fetchProgrammes = async (loadMore = false) => {
    try {
      setProgrammeLoading(true);
      const offset = loadMore ? programmeOffset : 0;
      const endpoint = `${EntityConstants.GET_PROGRAMME_DETAILS}?limit=10&offset=${offset}`;

      const response = await GET(endpoint);
      const data = await response.json();

      if (data.data) {
        if (loadMore) {
          setProgrammeList((prev) => [...prev, ...data.data.results]);
        } else {
          setProgrammeList(data.data.results);
        }
        setProgrammeNext(data.data.next);
        setProgrammeOffset(offset + data.data.results.length);
      }
    } catch (error) {
      Toast({
        type: "error",
        title: t("designs:error"),
        message: t("designs:failed_fetch_programmes"),
      });
    } finally {
      setProgrammeLoading(false);
    }
  };

  const handleProgrammeScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 1 &&
      programmeNext &&
      !programmeLoading
    ) {
      fetchProgrammes(true);
    }
  };

  const fetchBatches = async (loadMore = false) => {
    if (!selectedProgrammeCode) return;

    try {
      setBatchLoading(true);
      const offset = loadMore ? batchOffset : 0;
      const endpoint = `${EntityConstants.GET_BATCH_DETAILS}?programme_code=${selectedProgrammeCode}&limit=10&offset=${offset}`;

      const response = await GET(endpoint);
      const data = await response.json();

      if (data.data) {
        if (loadMore) {
          setBatchList((prev) => [...prev, ...data.data.results]);
        } else {
          setBatchList(data.data.results);
        }
        setBatchNext(data.data.next);
        setBatchOffset(offset + data.data.results.length);
      }
    } catch (error) {
      Toast({
        type: "error",
        title: t("designs:error"),
        message: t("designs:failed_fetch_batches"),
      });
    } finally {
      setBatchLoading(false);
    }
  };

  const handleProgrammeSelect = (programme) => {
    setProgrammeName(programme.programme_name);
    setSelectedProgrammeCode(programme.programme_code);
    localStorage.setItem("programme_code", programme.programme_code);
    setBatchName("");
    setBatchList([]);
    setBatchNext(null);
    setBatchOffset(0);
    localStorage.setItem("batch_code", "");
    setProgrammeDropdownOpen(false);
  };

  const handleBatchSelect = (batch) => {
    setBatchName(batch.batch_name);
    localStorage.setItem("batch_code", batch.batch_code);
    setBatchDropdownOpen(false);
  };

  const handleBatchScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 1 &&
      batchNext &&
      !batchLoading
    ) {
      fetchBatches(true);
    }
  };

  const submitCertificateData = async (submission, isPreview = false) => {
    const formData = { ...submission };

    // Handle logo
    if (!formData.data.logo || formData.data.logo.length === 0) {
      if (orgLogo) {
        formData.data.logo = [
          {
            storage: "base64",
            name: "org_logo.svg",
            url: orgLogo,
            type: "image/svg+xml",
          },
        ];
      } else {
        try {
          const orgResponse = await GET(EntityConstants.GET_ORG_DETAILS);
          const orgData = await orgResponse.json();
          if (orgData.org_logo) {
            formData.data.logo = [
              {
                storage: "base64",
                name: "default_org_logo.svg",
                url: orgData.org_logo,
                type: "image/svg+xml",
              },
            ];
          }
        } catch (err) {
          if (!isPreview) {
            Toast({
              type: "error",
              title: t("designs:error"),
              message: t("designs:failed_api_request"),
            });
          }
          return;
        }
      }
    }

    // Validate required fields
    const certTitle = sessionStorage.getItem("certificate_title");
    const progName = sessionStorage.getItem("programme_name");
    const batchNm = sessionStorage.getItem("batch_name");
    const isCertTitleEmpty =
      !certTitle || certTitle === "" || whiteSpaceEmptyStrRegex.test(certTitle);
    const isProgrammeNameEmpty =
      !progName || progName === "" || whiteSpaceEmptyStrRegex.test(progName);
    const isBatchNameEmpty =
      !batchNm || batchNm === "" || whiteSpaceEmptyStrRegex.test(batchNm);

    const isVerificationAmountRequired =
      localStorage.getItem("verCheck") === "true";
    const isVerificationAmountEmpty =
      isVerificationAmountRequired &&
      (!localStorage.getItem("verCost") ||
        localStorage.getItem("verCost").trim() === "");

    if (
      isCertTitleEmpty ||
      isProgrammeNameEmpty ||
      isBatchNameEmpty ||
      isVerificationAmountEmpty
    ) {
      Toast({
        type: "error",
        title: t("designs:error"),
        message: t("designs:fill_all_required_fields"),
      });
      return;
    }

    if (isExpiryRequired) {
      const expiryDays = calculateExpiryDays(expiryValue, expiryUnit);

      if (!expiryValue || expiryDays === null || expiryDays <= 0) {
        Toast({
          type: "error",
          title: t("designs:error"),
          message: t("designs:expiry_date_required_error"),
        });
        return;
      }
    }

    const payload = {
      form_data: formData,
      credential_template_id: credential_template_id,
      certificate_name: sessionStorage.getItem("certificate_title"),
      programme_code: localStorage.getItem("programme_code") || "",
      batch_code: localStorage.getItem("batch_code") || "",
      certificate_redirect_url:
        sessionStorage.getItem("certificate_redirect_url") || undefined,
      is_auth_required:
        sessionStorage.getItem("is_auth_required") !== null
          ? JSON.parse(sessionStorage.getItem("is_auth_required"))
          : undefined,
      is_public:
        sessionStorage.getItem("is_public") !== null
          ? JSON.parse(sessionStorage.getItem("is_public"))
          : undefined,
      is_pre_auth:
        sessionStorage.getItem("is_pre_auth") !== null
          ? JSON.parse(sessionStorage.getItem("is_pre_auth"))
          : undefined,
      is_payment_required: localStorage.getItem("verCheck") === "true",
      verification_cost: localStorage.getItem("verCheck")
        ? localStorage.getItem("verCost")
        : null,
      verification_fields: {
        components: selectedComponents.filter((comp) => comp.selected),
      },
    };

    payload.is_expires = isExpiryRequired;

    if (isExpiryRequired) {
      payload.is_expires = true;
      payload.expiry_config = {
        expires_in_days: calculateExpiryDays(expiryValue, expiryUnit),
        expires_value: Number(expiryValue),
        expires_unit: expiryUnit,
      };
    }

    try {
      setLoading(true);
      const response = await POST(
        `${EntityConstants.POST_CREDENTIALS_TEMPLATE_DESIGN}${credential_template_id}/design`,
        payload,
      );

      if (response.ok) {
        const data = await response.json();

        const essentialData = {
          data: Object.keys(submission.data).reduce((acc, key) => {
            const value = submission.data[key];
            if (typeof value === "string" && !value.startsWith("data:image")) {
              acc[key] = value;
            }
            return acc;
          }, {}),

          metadata: {
            hasLogo: !!submission.data.logo,
            fieldCount: Object.keys(submission.data).length,
            timestamp: new Date().toISOString(),
          },
        };
        sessionStorage.setItem(
          "issuer_credential_template_id",
          data.issuer_credential_template_id,
        );
        sessionStorage.setItem(
          "details_form_data",
          JSON.stringify(essentialData),
        );

        const programmeCode =
          selectedProgrammeCode || localStorage.getItem("programme_code") || "";
        if (!programmeCode) {
          Toast({
            type: "error",
            title: t("designs:error"),
            message: t("designs:required_programme_name"),
          });
          return null;
        }

        navigate(
          `/issuer/certificate/issue/${data.credential_template_id}/${data.issuer_credential_template_id}/${programmeCode}`,
        );

        Toast({
          type: "success",
          title: t("designs:success"),
          message: t("designs:saved_template"),
        });
        return data;
      } else {
        const errorData = await response.json();
        if (errorData.message && Array.isArray(errorData.message)) {
          errorData.message.forEach((msg) => {
            Toast({
              type: "error",
              title: t("designs:error"),
              message: msg,
            });
          });
        } else {
          Toast({
            type: "error",
            title: t("designs:error"),
            message: t("designs:failed_api_request"),
          });
        }
        return null;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    localStorage.setItem("advanced_settings_submit", "true");

    if (formRef.current) {
      const form = formRef.current;
      try {
        const submission = await form.submit();
        await submitCertificateData(submission);
      } catch (err) {
        console.error("Form submission error:", err);
      } finally {
        localStorage.removeItem("advanced_settings_submit");
      }
    }
  };

  const onSubmitHandle = async (sub, bool) => {
    if (bool) {
      await submitCertificateData(sub, true);
    } else {
      authContext.setTriggerLoginModuleToggleValue(true);
    }
  };

  const GET_CERTIFICATE_DATA = (credential_template_id) => {
    GET(
      `${EntityConstants.GET_CREDENTIALS_TEMPLATE_DESIGN}${credential_template_id}/design`,
    )
      .then((response) => response.json())
      .then((data) => {
        setCertificateData(data);
        setFormData(data?.default_template_variables_data?.data || {});
        const expiryState = getExpiryStateFromData(data);
        setIsExpiryRequired(expiryState.isExpiryRequired);
        setExpiryValue(expiryState.expiryValue);
        setExpiryUnit(expiryState.expiryUnit);
        if (!preAuthUserEditedRef.current) {
          setIsPreAuth(data?.is_pre_auth === true);
        }
      });
  };

  const GET_TEMPLATE_FORM_DATA = async (credential_template_id) => {
    try {
      const certificateResponse = await GET(
        `${EntityConstants.GET_CREDENTIALS_TEMPLATE_DESIGN}${credential_template_id}/design`,
      );
      const certificateData = await certificateResponse.json();

      if (certificateData?.form_io_url) {
        const formComponentsResponse = await fetch(certificateData.form_io_url);
        const formComponents = await formComponentsResponse.json();
        const hasFields = hasFormDataAttributes(formComponents);
        setHasFormFields(hasFields);
      } else {
        setHasFormFields(false);
      }
    } catch (error) {
      console.error("Error loading template form data:", error);
      setHasFormFields(false);
    }
  };

  const GET_VERIFICATION_SETTINGS = async () => {
    try {
      const response = await GET(EntityConstants.POST_VERIFICATION_SETTINGS);
      if (!response.ok) {
        throw new Error("Failed to fetch verification settings");
      }
      const data = await response.json();

      // Store values in localStorage
      localStorage.setItem(
        "verCheck",
        JSON.stringify(data.is_payment_required_for_vc),
      );
      localStorage.setItem(
        "verCost",
        JSON.stringify(data.verification_cost_vc),
      );

      setIsPaymentRequired(data.is_payment_required_for_vc);
      setVerificationCost(String(data.verification_cost_vc / 100 || "")); // Convert to rupees and ensure it's a string
    } catch (error) {
      console.error("Error fetching verification settings:", error);
    }
  };

  const formFieldsChanged = (fields) => {
    if (fields?.data) {
      setFormData(fields.data);
      setFieldsChanged(fields.data);

      if (fields.data.logo && fields.data.logo[0]?.url) {
        setOrgLogo(fields.data.logo[0].url);
      }
    }
    if (
      fields.changed?.component?.key !== undefined &&
      fields.changed?.value !== undefined
    ) {
      const fieldKey = fields.changed.component.key;
      if (fieldKey === "logo") {
        const logoValue = fields.changed.value;
        if (!logoValue || logoValue.length === 0 || !logoValue[0]?.url) {
          setOrgLogo(orgLogo);
        } else if (imgRegex.test(logoValue[0].url)) {
          // If the logo is valid, update the orgLogo state
          setOrgLogo(logoValue[0].url);
        }
      }
      if (
        fields.changed?.value[0]?.type &&
        imgRegex.test(fields.changed?.value[0].url)
      ) {
        isToggleOn
          ? setFieldsChanged((prevData) => ({
              ...prevData,
              // Add your new key-value pair here
              [fields.changed?.component?.key]: fields.changed?.value[0].url,
            }))
          : setCustomFieldChanged((prevData) => ({
              ...prevData,
              // Add your new key-value pair here
              [fields.changed?.component?.key]: fields.changed?.value[0].url,
            }));
      } else {
        isToggleOn
          ? setFieldsChanged((prevData) => ({
              ...prevData,
              // Add your new key-value pair here
              [fields.changed?.component?.key]: fields.changed?.value,
            }))
          : setCustomFieldChanged((prevData) => ({
              ...prevData,
              // Add your new key-value pair here
              [fields.changed?.component?.key]: fields.changed?.value,
            }));
      }
    } else if (fields?.data) {
      setFieldsChanged(fields.data);
    }
  };

  useEffect(() => {
    preAuthUserEditedRef.current = false;
    credential_template_id && GET_CERTIFICATE_DATA(credential_template_id);
    credential_template_id && GET_TEMPLATE_FORM_DATA(credential_template_id);
    GET_VERIFICATION_SETTINGS();
  }, [credential_template_id]);

  const fetchSvg = async (certificateData) => {
    const { template_variables_data, template_svg, template_html } =
      certificateData;
    const { templateType } = resolveTemplateSource(certificateData);
    try {
      const response = await fetch(`${template_html || template_svg}`);
      if (response.ok) {
        const svgContent = await response.text();
        setPreviewTemplateType(templateType);
        setCurrentSVG(svgContent);
        setFieldsChanged(template_variables_data);
        //!injecting the svg (or html) content into the container
        const container = document.getElementById("certificate_container");
        container.style.filter = "drop-shadow(0 0 2px rgba(0, 0, 0, 0.264))";
        injectTemplatePreview(container, svgContent, templateType);
      } else if (response.status === 404) {
        renderToContainer(
          "certificate_container",
          <div className="text-black flex justify-center w-full h-full text-lg">
            <div className="flex flex-col items-center gap-2 w-fit h-fit">
              <span className="flex items-center gap-2 w-fit h-fit  bg-gray-100 px-2 py-1 rounded-lg">
                <p>{t("designs:could_not_find_image")}</p>
                <LuImageOff className="text-cfPrimaryCardHover " />
              </span>
              <span>
                <p>{t("designs:create_new_template")}</p>
              </span>
            </div>
          </div>,
        );
      } else {
        renderToContainer(
          "certificate_container",
          <div className="text-black flex justify-center w-full h-full text-lg">
            <div
              className="flex items-center gap-2 w-fit h-fit rounded-lg cursor-pointer"
              onClick={() => {
                renderToContainer(
                  "certificate_container",
                  <span className="w-full flex justify-center">
                    <CfLoader />
                  </span>,
                );
                certificateData && fetchSvg(certificateData);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  renderToContainer(
                    "certificate_container",
                    <span className="w-full flex justify-center">
                      <CfLoader />
                    </span>,
                  );
                  certificateData && fetchSvg(certificateData);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <p>{t("designs:could_not_load_image")}</p>
              <RxReload className="text-cfPrimaryCardHover " />
            </div>
          </div>,
        );
      }
    } catch (error) {
      console.error("Error loading SVG:", error);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem("verCost");
      localStorage.removeItem("verCheck");
      localStorage.removeItem("programme_code");
      localStorage.removeItem("batch_code");
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      localStorage.removeItem("verCost");
      localStorage.removeItem("verCheck");
      localStorage.removeItem("programme_code");
      localStorage.removeItem("batch_code");
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        programmeDropdownRef.current &&
        !programmeDropdownRef.current.contains(event.target)
      ) {
        setProgrammeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        batchDropdownRef.current &&
        !batchDropdownRef.current.contains(event.target)
      ) {
        setBatchDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (authContext.isLoggedIn) {
      GET(EntityConstants.GET_ORG_DETAILS)
        .then((response) => response.json())
        .then((data) => {
          setOrgLogo(data.org_logo);
        })
        .catch(() => {
          Toast({
            type: "error",
            title: t("designs:error"),
            message: t("designs:api_request_failed"),
          });
        });
    }
  }, [authContext.isLoggedIn]);

  useEffect(() => {
    if (
      certificateData &&
      typeof certificateData === "object" &&
      Object.prototype.hasOwnProperty.call(
        certificateData,
        "default_template_variables_data",
      )
    ) {
      fetchSvg(certificateData);
    }
  }, [certificateData]);

  useEffect(() => {
    if (certificateData) {
      setProgrammeName(certificateData.programme_name || "");
      setBatchName(certificateData.batch_name || "");
      localStorage.setItem(
        "programme_code",
        certificateData.programme_code || "",
      );
      localStorage.setItem("batch_code", certificateData.batch_code || "");
    }
  }, [certificateData]);

  useEffect(() => {
    if (currentSVG) {
      const updatedFields = isToggleOn
        ? { ...fieldsChanged, logo: orgLogo }
        : { ...customFieldChanged, logo: orgLogo };

      updateTemplatePreview(
        document.getElementById("certificate_container"),
        currentSVG,
        updatedFields,
        previewTemplateType,
      );
    }
  }, [
    fieldsChanged,
    customFieldChanged,
    currentSVG,
    previewTemplateType,
    orgLogo,
    isToggleOn,
  ]);

  useEffect(() => {
    if (certificateTitle.trim().length === 0) {
      setErrorKey("designs:required_certificate_name");
      sessionStorage.removeItem("certificate_title");
    } else {
      sessionStorage.setItem("certificate_title", certificateTitle);
      setErrorKey("");
    }
  }, [certificateTitle]);

  useEffect(() => {
    if (programmeName.trim().length === 0) {
      setProgrammeNameErrorKey("designs:required_programme_name");
      sessionStorage.removeItem("programme_name");
      localStorage.removeItem("programme_code");
    } else {
      sessionStorage.setItem("programme_name", programmeName);
      setProgrammeNameErrorKey("");

      const programme = programmeList.find(
        (p) => p.programme_name === programmeName,
      );
      if (programme) {
        localStorage.setItem("programme_code", programme.programme_code);
      }
    }
  }, [programmeName, programmeList]);

  useEffect(() => {
    if (batchName.trim().length === 0) {
      setBatchNameErrorKey("designs:required_batch_name");
      sessionStorage.removeItem("batch_name");
      localStorage.removeItem("batch_code");
    } else {
      sessionStorage.setItem("batch_name", batchName);
      setBatchNameErrorKey("");

      const batch = batchList.find((b) => b.batch_name === batchName);
      if (batch) {
        localStorage.setItem("batch_code", batch.batch_code);
      }
    }
  }, [batchName, batchList]);

  useEffect(() => {
    sessionStorage.setItem("is_auth_required", isAuthRequired);
  }, [isAuthRequired]);

  useEffect(() => {
    sessionStorage.setItem("is_public", isPublic);
  }, [isPublic]);

  useEffect(() => {
    sessionStorage.setItem("is_pre_auth", isPreAuth);
  }, [isPreAuth]);

  useEffect(() => {
    fetchProgrammes();
  }, []);

  useEffect(() => {
    if (!batchDropdownOpen) {
      setBatchOffset(0);
    }
  }, [batchDropdownOpen]);

  useEffect(() => {
    if (selectedProgrammeCode) {
      fetchBatches();
    }
  }, [selectedProgrammeCode]);

  useEffect(() => {
    if (programmeName && programmeList.length > 0) {
      const selectedProgramme = programmeList.find(
        (programme) => programme.programme_name === programmeName,
      );
      if (selectedProgramme) {
        setSelectedProgrammeCode(selectedProgramme.programme_code);
      }
    }
  }, [programmeName, programmeList]);

  useEffect(() => {
    if (!certificateData || programmeName || programmeList.length !== 1) return;
    if (certificateData.programme_name || certificateData.programme_code) return;

    handleProgrammeSelect(programmeList[0]);
  }, [certificateData, programmeList, programmeName]);

  useEffect(() => {
    if (!selectedProgrammeCode || batchName || batchList.length !== 1) return;
    if (
      certificateData?.programme_code === selectedProgrammeCode &&
      (certificateData?.batch_name || certificateData?.batch_code)
    ) {
      return;
    }

    handleBatchSelect(batchList[0]);
  }, [selectedProgrammeCode, batchName, batchList, certificateData]);

  useEffect(() => {
    const prefillToggled = lastPrefillRef.current !== isToggleOn;
    lastPrefillRef.current = isToggleOn;
    let initialFormData = { ...formData };
    if (prefillToggled && isToggleOn) {
      valuesBeforePrefillRef.current = formData;
      const defaults = certificateData?.default_template_variables_data;
      initialFormData = {
        ...(defaults?.data && typeof defaults.data === "object"
          ? defaults.data
          : defaults),
      };
      delete initialFormData.submit;
    } else if (prefillToggled) {
      initialFormData = { ...(valuesBeforePrefillRef.current || formData) };
      valuesBeforePrefillRef.current = null;
    }

    if (certificateData?.form_io_url) {
      const historyFormData = sessionStorage.getItem("details_form_data");

      if (historyFormData) {
        sessionStorage.removeItem("details_form_data");
        fetch(certificateData?.form_io_url)
          .then((resp) => resp.json())
          .then((data) => {
            certificateData?.form_io_url &&
              Formio.createForm(
                document.getElementById("formio"),
                data,
                {},
              ).then((form) => {
                formRef.current = form;
                form.ready.then(() => {
                  form.submission = {
                    data: { ...JSON.parse(historyFormData).data },
                  };
                  form.on("change", formFieldsChanged);
                  form.on("submit", (sub) => {
                    if (
                      localStorage.getItem("advanced_settings_submit") ===
                      "true"
                    ) {
                      localStorage.removeItem("advanced_settings_submit");
                      return;
                    }
                    onSubmitHandle(sub, authContext.isLoggedIn);
                  });
                });
              });
            sessionStorage.removeItem("issuer_credential_template_id");
          })
          .catch((error) =>
            console.log("Error while hitting formio url: ", error),
          );
      } else {
        fetch(certificateData?.form_io_url)
          .then((resp) => resp.json())
          .then((data) => {
            certificateData?.form_io_url &&
              Formio.createForm(
                document.getElementById("formio"),
                data,
                {},
              ).then((form) => {
                formRef.current = form;
                form.ready.then(() => {
                  form.submission = { data: initialFormData };
                  form.on("change", formFieldsChanged);
                  form.on("submit", (sub) => {
                    if (
                      localStorage.getItem("advanced_settings_submit") ===
                      "true"
                    ) {
                      localStorage.removeItem("advanced_settings_submit");
                      return;
                    }
                    onSubmitHandle(sub, authContext.isLoggedIn);
                  });
                });
              });
            sessionStorage.removeItem("issuer_credential_template_id");
          })
          .catch((error) =>
            console.log("Error while hitting formio url: ", error),
          );
      }
    }
  }, [
    certificateData?.form_io_url,
    authContext.isLoggedIn,
    isToggleOn,
    activeTab,
  ]);

  useEffect(() => {
    authContext.lastVisitedPathHandler(location.pathname);
    const historyCertfificateName = sessionStorage.getItem("certificate_title");
    const historyProgrammeName = sessionStorage.getItem("programme_name");
    const historyBatchName = sessionStorage.getItem("batch_name");
    if (historyCertfificateName) {
      setCertificateTitle(historyCertfificateName);
    }
    if (historyProgrammeName) {
      setProgrammeName(historyProgrammeName);
    }
    if (historyBatchName) {
      setBatchName(historyBatchName);
    }
  }, []);

  useEffect(() => {
    if (!programmeDropdownOpen) {
      setProgrammeOffset(0);
    }
  }, [programmeDropdownOpen]);

  const GET_SUBJECT_CERTIFICATE_DATA = async (credential_template_id) => {
    try {
      const formResponse = await GET(
        `${EntityConstants.GET_CREDENTIALS_SUBJECT_FORM}${credential_template_id}/subject?type=form`,
      );
      const formData = await formResponse.json();

      const formComponentsResponse = await fetch(
        formData.credential_subject_form,
      );
      const formComponents = await formComponentsResponse.json();

      const draftResponse = await GET(
        `${EntityConstants.GET_CREDENTIALS_TEMPLATE_DESIGN}${credential_template_id}/design`,
      );
      const draftData = await draftResponse.json();

      // Get the current verification fields or initialize empty array
      const currentVerificationFields =
        draftData.verification_fields?.components || [];
      const currentSelectedKeys = currentVerificationFields.map((c) => c.key);

      const filteredComponents = formComponents.components
        .filter((comp) => comp.key !== "submit")
        .map((comp) => ({
          ...comp,
          selected: currentSelectedKeys.includes(comp.key),
        }));

      setSelectedComponents(filteredComponents);
    } catch (error) {
      console.error("Error loading subject data:", error);
    }
  };

  useEffect(() => {
    GET_SUBJECT_CERTIFICATE_DATA(credential_template_id);
  }, []);

  const handleCheckboxChange = (componentKey) => {
    setSelectedComponents((prevComponents) =>
      prevComponents.map((comp) =>
        comp.key === componentKey
          ? { ...comp, selected: !comp.selected }
          : comp,
      ),
    );
  };

  const handleAuthRequiredChange = (e) => {
    const newAuthRequired = e.target.checked;
    if (newAuthRequired && isPublic) {
      Toast({
        type: "error",
        title: t("designs:invalid_combination"),
        message: t("designs:auth_public_not_allowed"),
      });
      return;
    }
    setIsAuthRequired(newAuthRequired);
  };

  const handleIsPublicChange = (e) => {
    const newIsPublic = e.target.checked;
    if (newIsPublic && isAuthRequired) {
      Toast({
        type: "error",
        title: t("designs:invalid_combination"),
        message: t("designs:auth_public_not_allowed_message"),
      });
      return;
    }
    setIsPublic(newIsPublic);
  };

  const handlePreAuthChange = (e) => {
    preAuthUserEditedRef.current = true;
    setIsPreAuth(e.target.checked);
  };

  useEffect(() => {
    const validateToggleCombinations = () => {
      const hasAuth = isAuthRequired;
      const isPub = isPublic;
      const hasPayment = isPaymentRequired;

      const allDisabled = !hasPayment && !hasAuth && !isPub;
      if (allDisabled) {
        setIsInvalidCombination(false);
        setToggleMessage("");
        return;
      }

      if (hasAuth && isPub) {
        setIsInvalidCombination(true);
        setToggleMessage(t("designs:invalid_combination_auth_public"));
        return;
      }
      setIsInvalidCombination(false);

      if (hasPayment && hasAuth && isPub) {
        setToggleMessage(
          <span>• {t("designs:conflict_option_auth_public")}</span>,
        );
      } else if (hasPayment && hasAuth) {
        setToggleMessage(
          <span>
            • {t("designs:paid_verification_auth_required_description_1")}
            <br />• {t("designs:paid_verification_auth_required_description_2")}
          </span>,
        );
      } else if (hasPayment && isPub) {
        setToggleMessage(
          <span>
            • {t("designs:paid_verification_is_public_description_1")}
            <br />• {t("designs:paid_verification_is_public_description_2")}
          </span>,
        );
      } else if (hasAuth && isPub) {
        setToggleMessage(<span>• {t("designs:invalid_combination")}</span>);
      } else if (hasPayment) {
        setToggleMessage(
          <span>• {t("designs:paid_verification_description")}</span>,
        );
      } else if (hasAuth) {
        setToggleMessage(
          <span>• {t("designs:auth_required_description")}</span>,
        );
      } else if (isPub) {
        setToggleMessage(<span>• {t("designs:is_public_description")}</span>);
      }
    };

    validateToggleCombinations();
  }, [isPaymentRequired, isAuthRequired, isPublic, t]);

  return (
    <>
      {breadcrumbItems.length > 0 && (
        <CfBreadcrumb
          breadcrumbItems={breadcrumbItems}
          isToggleOn={isToggleOn}
          setIsToggleOn={setIsToggleOn}
          isPostPaid={true}
          hasFormFields={hasFormFields}
          testId="certificate-details-breadcrumb"
        />
      )}

      <div
        className="flex flex-col md:flex-row w-full"
        id={pageRootTestIds.certificateDetails}
      >
        <div className="w-full md:w-[30%] md:shrink-0 max-h-none md:max-h-[84vh] md:min-h-[84vh] overflow-y-auto flex flex-col items-center bg-cfSecondary min-h-full">
          <div className="flex space-x-0 md:space-x-4 mb-2 mt-4 bg-gray-300 rounded-xl md:rounded-2xl p-1 mx-1 md:mx-0">
            <button
              className={`px-2 md:px-3 py-1 transition-all duration-200 ${
                activeTab === "basic"
                  ? "bg-white text-black rounded-xl border-none focus:outline-none focus:ring-0"
                  : "bg-gray-300 rounded-lg border-none focus:outline-none focus:ring-0"
              }`}
              onClick={() => handleTabChange("basic")}
            >
              {t("designs:basic_settings")}
            </button>
            <button
              className={`px-1 md:px-3 py-1 transition-all duration-200 ${
                activeTab === "advanced"
                  ? "bg-white text-black rounded-xl border-none focus:outline-none focus:ring-0"
                  : "bg-gray-300 rounded-lg border-none focus:outline-none focus:ring-0"
              }`}
              onClick={() => handleTabChange("advanced")}
            >
              {t("designs:advanced_settings")}
            </button>
          </div>

          <div className="w-full px-4 py-2">
            {activeTab === "basic" && (
              <div className="flex flex-col w-full items-start">
                <label
                  htmlFor="certificateName"
                  className="after:content-['*'] after:text-red-600"
                >
                  {t("designs:certificate_name")}
                </label>
                <div className="flex justify-between items-center focus-within:border-4 focus-within:border-cfCertificateNameInputBorder rounded-xl w-full">
                  <div className="flex justify-between items-center border-[1px] rounded-md p-1 w-full">
                    <input
                      type="text"
                      name="certificateName"
                      className="border-none rounded-md w-full focus:outline-none"
                      id="certificateName"
                      onChange={(e) => {
                        const value = e.target.value;
                        setCertificateTitle(value);
                      }}
                      value={certificateTitle}
                    />
                  </div>
                </div>
                {errorKey && <p className="text-red-600">{t(errorKey)}</p>}

                <label
                  htmlFor="programmeName"
                  className="mt-4 after:content-['*'] after:text-red-600"
                >
                  {t("designs:programme_name")}
                </label>
                <div className="relative w-full" ref={programmeDropdownRef}>
                  <div
                    className="flex justify-between items-center border-[1px] rounded-md p-1 w-full cursor-pointer"
                    onClick={() => {
                      setProgrammeDropdownOpen(!programmeDropdownOpen);
                      if (
                        !programmeDropdownOpen &&
                        programmeList.length === 0
                      ) {
                        fetchProgrammes();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        setProgrammeDropdownOpen(!programmeDropdownOpen);
                        if (
                          !programmeDropdownOpen &&
                          programmeList.length === 0
                        ) {
                          fetchProgrammes();
                        }
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <input
                      type="text"
                      name="programmeName"
                      className="border-none rounded-md w-full focus:outline-none cursor-pointer"
                      id="programmeName"
                      value={programmeName}
                      readOnly
                      placeholder="Select a programme"
                    />
                    <svg
                      className={`w-5 h-5 ml-2 transition-transform duration-200 ${
                        programmeDropdownOpen ? "transform rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                  {programmeDropdownOpen && (
                    <div
                      className="absolute z-10 mt-1 w-full max-h-60 overflow-auto border border-gray-300 bg-white rounded-md shadow-lg"
                      onScroll={handleProgrammeScroll}
                    >
                      {programmeList.length > 0 ? (
                        programmeList.map((programme) => (
                          <div
                            key={programme.programme_code}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              handleProgrammeSelect(programme);
                              setProgrammeName(programme.programme_name);
                              setProgrammeDropdownOpen(false);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                handleProgrammeSelect(programme);
                                setProgrammeName(programme.programme_name);
                                setProgrammeDropdownOpen(false);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            {programme.programme_name}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-gray-500">
                          {programmeLoading
                            ? "Loading..."
                            : "No programmes found"}
                        </div>
                      )}
                      {programmeLoading && (
                        <div className="px-4 py-2 flex justify-center">
                          <CfLoader size="small" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {programmeNameErrorKey && (
                  <p className="text-red-600">{t(programmeNameErrorKey)}</p>
                )}

                {/* Add Batch Name field */}
                <label
                  htmlFor="batchName"
                  className="mt-4 after:content-['*'] after:text-red-600"
                >
                  {t("designs:batch_name")}
                </label>
                <div className="relative w-full" ref={batchDropdownRef}>
                  <div
                    className="flex justify-between items-center border-[1px] rounded-md p-1 w-full cursor-pointer"
                    onClick={() => {
                      if (!selectedProgrammeCode) {
                        Toast({
                          type: "warning",
                          title: "Warning",
                          message: "Please select a programme first",
                        });
                        return;
                      }
                      setBatchDropdownOpen(!batchDropdownOpen);
                      if (!batchDropdownOpen && batchList.length === 0) {
                        fetchBatches();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        if (!selectedProgrammeCode) {
                          Toast({
                            type: "warning",
                            title: "Warning",
                            message: "Please select a programme first",
                          });
                          return;
                        }
                        setBatchDropdownOpen(!batchDropdownOpen);
                        if (!batchDropdownOpen && batchList.length === 0) {
                          fetchBatches();
                        }
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    style={{
                      backgroundColor: !selectedProgrammeCode
                        ? "#f3f4f6"
                        : "inherit",
                      cursor: !selectedProgrammeCode
                        ? "not-allowed"
                        : "pointer",
                    }}
                  >
                    <input
                      type="text"
                      name="batchName"
                      className="border-none rounded-md w-full focus:outline-none cursor-pointer"
                      id="batchName"
                      value={batchName}
                      readOnly
                      placeholder={
                        !selectedProgrammeCode
                          ? "Select a programme first"
                          : "Select a batch"
                      }
                    />
                    <svg
                      className={`w-5 h-5 ml-2 transition-transform duration-200 ${
                        batchDropdownOpen ? "transform rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                  {batchDropdownOpen && (
                    <div
                      className="absolute z-10 mt-1 w-full max-h-60 overflow-auto border border-gray-300 bg-white rounded-md shadow-lg"
                      onScroll={handleBatchScroll}
                    >
                      {batchList.length > 0 ? (
                        batchList.map((batch) => (
                          <div
                            key={batch.batch_code}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              handleBatchSelect(batch);
                              setBatchName(batch.batch_name);
                              setBatchDropdownOpen(false);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                handleBatchSelect(batch);
                                setBatchName(batch.batch_name);
                                setBatchDropdownOpen(false);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            {batch.batch_name}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-gray-500">
                          {batchLoading ? "Loading..." : "No batches found"}
                        </div>
                      )}
                      {batchLoading && (
                        <div className="px-4 py-2 flex justify-center">
                          <CfLoader size="small" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {batchNameErrorKey && (
                  <p className="text-red-600">{t(batchNameErrorKey)}</p>
                )}

                <div id="formio" className="w-full py-2 h-full">
                  <div className="flex justify-center items-center">
                    <CfLoader />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "advanced" && (
              <div className="advanced-settings">
                <div className="flex flex-col mb-4 mt-2">
                  <div
                    htmlFor="verification-fields"
                    className="text-gray-800 font-semibold"
                  >
                    {t("designs:certificate_delivery")}
                  </div>
                  <div className="text-gray-600 text-sm">
                    {t("designs:certificate_delivery_description")}
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <SettingOption
                    key={"toggle_is_payment_required"}
                    toggleStatus={isPaymentRequired}
                    heading={t("designs:enable_paid_verification")}
                    onclickFunction={handlePaymentCheckboxChange}
                    label={t("designs:enable_paid_verification")}
                  />
                </div>
                {isPaymentRequired && (
                  <div className="mb-4">
                    <label htmlFor="price" className="text-gray-600">
                      {t("designs:verification_amount_to_pay")}
                    </label>
                    <div className="flex items-center rounded-md bg-white pl-3 outline outline-1 -outline-offset-1 outline-gray-300 has-[input:focus-within]:outline has-[input:focus-within]:outline-2 has-[input:focus-within]:-outline-offset-2 has-[input:focus-within]:outline-indigo-600">
                      <div className="shrink-0 select-none text-base text-gray-500 sm:text-sm/6 mr-2.5">
                        ₹
                      </div>
                      <input
                        type="number"
                        name="price"
                        id="price"
                        min="0.01"
                        className="block min-w-0 grow py-1.5 pl-1 pr-3 text-base text-gray-800 placeholder:text-gray-400 focus:outline focus:outline-0 sm:text-sm/6 border border-gray-500"
                        placeholder="0.00"
                        value={verificationCost}
                        onChange={handleVerificationCostChange}
                        onWheel={(e) => e.target.blur()}
                      />
                    </div>

                    {!verificationCost.trim() && (
                      <p className="text-red-600 mt-1">
                        {t("designs:required_paid_verification")}
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <SettingOption
                    key={"toggle_is_expiry_required"}
                    toggleStatus={isExpiryRequired}
                    heading={t("designs:is_expiry_required")}
                    onclickFunction={handleExpiryRequiredChange}
                    label={t("designs:is_expiry_required")}
                  />
                </div>

                {isExpiryRequired && (
                  <div className="mb-4">
                    <label htmlFor="expiry_duration" className="text-gray-600">
                      {t("designs:expiry_duration_label")}
                    </label>
                    <div className="mt-1 flex items-center rounded-md bg-white pl-3 pr-3 outline outline-1 -outline-offset-1 outline-gray-300 has-[input:focus-within]:outline has-[input:focus-within]:outline-2 has-[input:focus-within]:-outline-offset-2 has-[input:focus-within]:outline-indigo-600 has-[select:focus]:outline has-[select:focus]:outline-2 has-[select:focus]:-outline-offset-2 has-[select:focus]:outline-indigo-600">
                      <div className="flex-1">
                        <input
                          type="number"
                          min="1"
                          step="1"
                          id="expiry_duration"
                          name="expiry_duration"
                          className="block w-full min-w-0 py-1.5 pl-1 pr-3 text-base text-gray-800 placeholder:text-gray-400 focus:outline focus:outline-0 sm:text-sm/6 border-none bg-transparent"
                          placeholder={t("designs:enter_expiry_value")}
                          value={expiryValue}
                          onChange={handleExpiryValueChange}
                          onWheel={(e) => e.target.blur()}
                        />
                      </div>
                      <div className="h-6 w-px bg-gray-300 mx-2 shrink-0" />
                      <div className="w-[112px] shrink-0">
                        <Dropdown
                          options={expiryUnitOptions}
                          selected={
                            expiryUnitOptions.find(
                              (option) => option.value === expiryUnit,
                            ) || expiryUnitOptions[0]
                          }
                          onSelect={handleExpiryUnitChange}
                          testId="certificate-details-dropdown-expiry-unit"
                          buttonClass="border-0 bg-transparent px-2 py-1.5 text-sm text-gray-800 shadow-none"
                          menuClass="right-0 left-auto mt-1 min-w-[120px]"
                        />
                      </div>
                    </div>
                    {(!expiryValue ||
                      calculateExpiryDays(expiryValue, expiryUnit) <= 0) && (
                      <p className="text-red-600 mt-1">
                        {t("designs:expiry_date_required_error")}
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <SettingOption
                    key={"toggle_is_auth_required"}
                    toggleStatus={isAuthRequired}
                    heading={t("designs:is_auth_required")}
                    onclickFunction={handleAuthRequiredChange}
                    label={t("designs:is_auth_required")}
                    disabled={isPublic}
                  />
                </div>

                <div>
                  <SettingOption
                    key={"toggle_is_public"}
                    toggleStatus={isPublic}
                    heading={t("designs:is_public")}
                    onclickFunction={handleIsPublicChange}
                    label={t("designs:is_public")}
                    disabled={isAuthRequired}
                  />
                </div>

                <div>
                  <SettingOption
                    key={"toggle_is_pre_auth"}
                    toggleStatus={isPreAuth}
                    heading={t("designs:is_pre_auth_required")}
                    onclickFunction={handlePreAuthChange}
                    label={t("designs:is_pre_auth_required")}
                    testId="toggle-is-pre-auth"
                  />
                </div>

                {(toggleMessage || isPreAuth) && (
                  <div
                    className={`mb-4 p-4 rounded-lg border-none ${
                      isInvalidCombination
                        ? "bg-red-50 border-red-200 text-red-800"
                        : "bg-blue-50 border-blue-200 text-cfPrimaryButton"
                    }`}
                  >
                    <h4 className="font-semibold mb-2">
                      {t("designs:current_settings")}
                    </h4>
                    <div className="text-sm space-y-2">
                      {toggleMessage &&
                        (typeof toggleMessage === "string" ? (
                          <div className="whitespace-pre-line">
                            {toggleMessage}
                          </div>
                        ) : (
                          <div>{toggleMessage}</div>
                        ))}
                      {isPreAuth && (
                        <div>• {t("designs:pre_auth_enabled_hint")}</div>
                      )}
                    </div>
                  </div>
                )}

                {selectedComponents.length > 0 && (
                  <div className="mb-4">
                    <div className="flex flex-col mb-4">
                      <div
                        htmlFor="verification-fields"
                        className="text-gray-800 font-semibold"
                      >
                        {t("designs:verification_fields")}
                      </div>
                      <div className="text-gray-600 text-sm">
                        {t("designs:verification_fields_description")}
                      </div>
                    </div>
                    <div
                      id="verification-fields"
                      className="max-h-60 overflow-y-auto border rounded p-2"
                    >
                      {selectedComponents.map((component) => (
                        <div
                          key={component.key}
                          className="flex items-center mb-2"
                        >
                          <input
                            type="checkbox"
                            checked={component.selected}
                            onChange={() => handleCheckboxChange(component.key)}
                            className="mr-2 mb-2 rounded-lg"
                            id={`checkbox-${component.key}`}
                          />
                          <label htmlFor={`checkbox-${component.key}`}>
                            {component.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  className="submit-btn mt-6 px-4 py-2 bg-cfPrimaryButton text-cfSecondary w-full text-white rounded-md disabled:bg-gray-400 disabled:cursor-not-allowed disabled:rounded-md"
                  onClick={handleSubmit}
                  disabled={loading || isInvalidCombination}
                >
                  {isInvalidCombination
                    ? "Please fix invalid settings"
                    : "Submit"}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="w-full min-w-0 flex-1 p-3">
          <div className="w-full">
            <div
              className="flex flex-col flex-shrink max-h-[60vh] sm:max-h-[73vh] overflow-auto"
              id="certificate_container"
            >
              <CfLoader />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CertificateDetails;
